import {
    Box,
    Button,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    Text,
    Tooltip,
    useToast,
} from "@chakra-ui/react";
import {useApolloClient, useLazyQuery, useMutation} from "@apollo/client";
import {createDraftMutation, publishDraftMutation, versionsQuery} from "./versionQueries";
import {MarkerSpot, PolygonSpot, Version} from "../../components/types";
import {AppContext, AppContextI} from "../../components/context/ContextWrapper";
import * as React from "react";
import {useContext, useEffect, useRef, useState} from "react";
import moment from "moment";
import {DATE_FORMAT_DA} from "./Versioning";
import {VersionDiffModal} from "./VersionDiffModal";
import {getAreaContentQuery} from "../../components/area/areaQueries";
import {getSpotsQuery} from "../../components/spots/graphQl";
import {getWalkLinesQuery} from "../../components/walkLines/graphQl";
import {detectCycles, describeCycles} from "../../components/walkLines/cycleDetection";

export const VersionSelector = () => {
    const appContext: AppContextI = useContext<AppContextI>(AppContext);
    const toast = useToast();
    const [selected, setSelected] = useState<number>(0);
    const [versions, setVersions] = useState<Version[] | undefined>(undefined);
    const [draftHasChanges, setDraftHasChanges] = useState<boolean>(false);
    const [isDiffOpen, setDiffOpen] = useState<boolean>(false);
    const [isPublishWithNoteOpen, setPublishWithNoteOpen] = useState<boolean>(false);
    const [isPublishing, setIsPublishing] = useState<boolean>(false);
    const [publishNote, setPublishNote] = useState<string>("");
    const publishNoteInputRef = useRef<HTMLInputElement>(null);
    const client = useApolloClient();

    const areaId = appContext.getArea()?.id;
    const isReadOnly = appContext.isReadOnly();
    const currentModifiedAt = appContext.getModifiedAt();
    const hasUnpublishedChanges = appContext.getArea()?.hasUnpublishedChanges;
    const displayedVersion = appContext.getDisplayedVersion();

    const [publishDraft] = useMutation(publishDraftMutation);
    const [createDraft] = useMutation(createDraftMutation, {
        variables: {
            areaId: areaId
        }
    });

    const [loadVersions, {data, refetch, called}] = useLazyQuery(versionsQuery, {
        variables: {
            areaId: areaId
        }
    });

    useEffect(() => {
        if (areaId && !called) {
            loadVersions();
        }
    }, [areaId, called, loadVersions]);

    useEffect(() => {
        if (currentModifiedAt <= 0) {
            return;
        }

        setDraftHasChanges(true);

        // TODO: Looks like something is not done saving when we refetch - timeout is a workaround for now...
        const timeoutId = setTimeout(() => {
            refetch();
        }, 200);

        return () => clearTimeout(timeoutId);
    }, [currentModifiedAt, refetch]);

    useEffect(() => {
        if (hasUnpublishedChanges === true) {
            setDraftHasChanges(true);
            return;
        }
        if (hasUnpublishedChanges === false) {
            setDraftHasChanges(false);
        }
    }, [hasUnpublishedChanges]);

    useEffect(() => {
        if (data) {
            const dVersion = data?.getVersions.find((version: Version) => version.publishedAt === 0);
            if (dVersion) {
                setVersions([dVersion, ...data?.getVersions]);
            } else {
                setVersions(data?.getVersions);
            }
        }
    }, [data])

    useEffect(() => {
        if (versions && displayedVersion) {
            const indx = versions.findIndex((version: Version) => version.id === displayedVersion.id)
            setSelected(indx);
        }
    }, [displayedVersion, versions]);

    const onSelectionChange = (evt: any) => {
        const newSelected = evt.target.value;
        setSelected(newSelected);

        if (versions) {
            const version = versions[newSelected];
            appContext.setDisplayedVersion(version);
        }
    }

    const doPublish = async (releaseNote?: string) => {
        if (!areaId || !hasDraft || !hasDraftChanges) {
            return;
        }

        // Validate: block publish if spot rules contain cycles
        const spots = (appContext.getSpots() || []).filter((s): s is MarkerSpot | PolygonSpot => !!s && !!s.type);
        const cycleResult = detectCycles(spots);
        if (cycleResult.hasCycle) {
            const descriptions = describeCycles(spots, cycleResult);
            toast({
                title: "Cannot publish: cyclic spot dependencies detected",
                description: descriptions.join("\n"),
                status: "error",
                duration: 10000,
                isClosable: true,
            });
            return;
        }

        await publishDraft({
            variables: {
                areaId: areaId,
                releaseNote: releaseNote?.trim() || undefined
            }
        });

        const createdDraftResult = await createDraft();
        const nextDraft: Version | undefined = createdDraftResult.data?.createDraft;

        setDraftHasChanges(false);

        const refreshed = await refetch();
        const refreshedDraft = refreshed.data?.getVersions?.find((version: Version) => version.publishedAt === 0);
        const displayedDraft = nextDraft || refreshedDraft;

        if (displayedDraft) {
            appContext.setDisplayedVersion(displayedDraft);
        }

        await client.refetchQueries({
            include: [getAreaContentQuery, getSpotsQuery]
        });
    }

    const draftVersion = versions?.find((version: Version) => version.publishedAt === 0);
    const latestPublishedVersion = versions?.find((version: Version) => version.publishedAt > 0);
    const selectedVersion = versions && versions.length > selected ? versions[selected] : undefined;
    const hasDraft = !!draftVersion;
    const hasPublished = !!latestPublishedVersion;
    const hasDraftChanges = hasUnpublishedChanges === undefined
        ? draftHasChanges
        : hasUnpublishedChanges || draftHasChanges;
    const aliasDraftToLatestPublished = hasDraft && !hasDraftChanges && !!latestPublishedVersion;
    const baseVersion = selectedVersion && selectedVersion.publishedAt > 0
        ? selectedVersion
        : latestPublishedVersion;
    const canViewDiff = !!areaId && !!baseVersion && hasPublished;
    const compareLabel = hasDraft && hasDraftChanges ? "Draft" : "Published";

    const openDiff = () => {
        setDiffOpen(true);
    };

    const handleReverted = async () => {
        try {
            const result = await refetch();
            const draft = result.data?.getVersions?.find((version: Version) => version.publishedAt === 0);
            const latest = draft || result.data?.getVersions?.find((version: Version) => version.publishedAt > 0);
            if (latest) {
                appContext.setDisplayedVersion(latest);
            }
            setDraftHasChanges(false);
        } finally {
            await client.refetchQueries({
                include: [getAreaContentQuery, getSpotsQuery, getWalkLinesQuery]
            });
        }
    };

    const handleDraftCreated = async (draftVersion?: Version) => {
        try {
            await refetch();
            if (draftVersion) {
                appContext.setDisplayedVersion(draftVersion);
            } else {
                const nextDraft = versions?.find((version: Version) => version.publishedAt === 0);
                if (nextDraft) {
                    appContext.setDisplayedVersion(nextDraft);
                }
            }
        } finally {
            await client.refetchQueries({
                include: [getAreaContentQuery, getSpotsQuery]
            });
        }
    };

    const closeDiff = () => {
        setDiffOpen(false);
    };

    const closePublishWithNoteModal = () => {
        if (isPublishing) {
            return;
        }
        setPublishWithNoteOpen(false);
        setPublishNote("");
    };

    const openPublishWithNoteModal = () => {
        setPublishNote("");
        setIsPublishing(false);
        setPublishWithNoteOpen(true);
    };

    const handlePublishWithNote = async () => {
        if (isPublishing) {
            return;
        }

        setIsPublishing(true);

        try {
            await doPublish(publishNote);
            setPublishWithNoteOpen(false);
            setPublishNote("");
        } finally {
            setIsPublishing(false);
        }
    };

    const truncateReleaseNote = (note?: string, maxLength: number = 34) => {
        const trimmed = (note || "").trim();
        if (!trimmed) {
            return "";
        }
        if (trimmed.length <= maxLength) {
            return trimmed;
        }
        return `${trimmed.slice(0, Math.max(0, maxLength - 1)).trimEnd()}...`;
    };

    const formatVersionLabel = (version: Version) => {
        const baseLabel = moment(new Date(version.publishedAt)).format(DATE_FORMAT_DA);
        const noteLabel = truncateReleaseNote(version.releaseNote);
        const latestPublishedSuffix = !hasDraft && latestPublishedVersion?.id === version.id ? " (Latest published)" : "";
        const labelWithSuffix = `${baseLabel}${latestPublishedSuffix}`;
        return noteLabel ? `${labelWithSuffix} - ${noteLabel}` : labelWithSuffix;
    };
    const draftOptionLabel = aliasDraftToLatestPublished && latestPublishedVersion
        ? formatVersionLabel(latestPublishedVersion)
        : `Draft (${versions && moment(new Date(versions[0].modifiedAt)).format(DATE_FORMAT_DA)})`;

    const selectedReleaseNote = selectedVersion?.publishedAt
        ? (selectedVersion.releaseNote || "").trim()
        : "";

    return <div style={{
        display: "flex",
        columnGap: "8px",
        flexWrap: "nowrap",
        alignItems: "center"
    }}>
        <Select
            size={"xs"}
            value={selected}
            onChange={onSelectionChange}
            minW={"200px"}
            maxW={"260px"}
            flexShrink={1}
        >
            {hasDraft ?
                <option
                    key={"vDraft"}
                    value={0}>{draftOptionLabel}
                </option>
                : null
            }
            {versions?.filter((version: Version) => {
                if (version.publishedAt <= 0) {
                    return false;
                }
                return !(aliasDraftToLatestPublished && latestPublishedVersion && version.id === latestPublishedVersion.id);

            })
                .map((version: Version) =>
                    <option key={version.id}
                            value={versions.findIndex((candidate: Version) => candidate.id === version.id)}>
                        {formatVersionLabel(version)}
                    </option>)
            }
        </Select>
        {isReadOnly ? null : (
            <>
                    <Button
                        size={"xs"}
                        px={4}
                        width={"112px"}
                        minWidth={"112px"}
                        flexShrink={0}
                        whiteSpace={"nowrap"}
                        colorScheme={hasDraft ? "green" : "gray"}
                        onClick={openPublishWithNoteModal}
                        disabled={!appContext.isDraft() || !hasDraftChanges}
                    >
                        Publish
                    </Button>
                <Tooltip label={!canViewDiff ? "No published versions" : ""}>
                    <span>
                        <Button
                            size="xs"
                            px={3}
                            whiteSpace={"nowrap"}
                            variant="outline"
                            onClick={openDiff}
                            isDisabled={!canViewDiff}
                        >
                            View changes
                        </Button>
                    </span>
                </Tooltip>
                {selectedReleaseNote ? (
                    <Tooltip label={selectedReleaseNote} hasArrow>
                        <Box flex={1} minW={0} marginLeft={1} marginRight={2}>
                            <Text
                                fontSize={"xs"}
                                color={"gray.600"}
                                overflow={"hidden"}
                                textOverflow={"ellipsis"}
                                whiteSpace={"nowrap"}
                            >
                                {selectedReleaseNote}
                            </Text>
                        </Box>
                    </Tooltip>
                ) : null}
            </>
        )}
        <Modal
            isOpen={isPublishWithNoteOpen}
            onClose={closePublishWithNoteModal}
            isCentered
            initialFocusRef={publishNoteInputRef}
            closeOnEsc={!isPublishing}
            closeOnOverlayClick={!isPublishing}
        >
            <ModalOverlay/>
            <ModalContent>
                <ModalHeader>Publish with note</ModalHeader>
                <ModalCloseButton isDisabled={isPublishing}/>
                <ModalBody>
                    <Input
                        ref={publishNoteInputRef}
                        placeholder={"Enter version note"}
                        value={publishNote}
                        onChange={(event) => setPublishNote(event.target.value)}
                        isDisabled={isPublishing}
                        onKeyDown={(event) => {
                            if (event.key === "Enter" && !isPublishing) {
                                event.preventDefault();
                                handlePublishWithNote();
                            }
                        }}
                    />
                </ModalBody>
                <ModalFooter gap={2}>
                    <Button variant={"ghost"} onClick={closePublishWithNoteModal} isDisabled={isPublishing}>
                        Cancel
                    </Button>
                    <Button colorScheme={"green"} onClick={handlePublishWithNote} isDisabled={isPublishing} isLoading={isPublishing}>
                        Publish
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
        <VersionDiffModal
            isOpen={isDiffOpen}
            onClose={closeDiff}
            areaId={areaId}
            selectedVersion={selectedVersion}
            hasDraft={hasDraft}
            hasPublished={hasPublished}
            baseVersion={baseVersion}
            compareLabel={compareLabel}
            hasDraftChanges={hasDraftChanges}
            latestPublishedVersion={latestPublishedVersion}
            canRevert={!!areaId && hasDraft && !isReadOnly}
            onReverted={handleReverted}
            onDraftCreated={handleDraftCreated}
        />
    </div>
}
